import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/solid";
import { usePagination } from "@mantine/hooks";
import clsx from "clsx";
import { useCallback } from "react";

import { Button } from "@/features/common/components/Button";
import { usePaginationContext } from "@/features/common/contexts/usePaginationContext";

type WithCustomOnChangeProps = {
  preventDefaultOnChange: true;
  onChange: (page: number, defaultOnChange: (page: number) => void) => void;
};

type WithoutCustomOnChangeProps = {
  preventDefaultOnChange?: false;
  onChange?: undefined;
};

type PaginationProps = {
  className?: string;
  totalPages: number;
} & (WithCustomOnChangeProps | WithoutCustomOnChangeProps);

const arrowButtonClassName =
  "!px-2 disabled:bg-transparent disabled:border-transparent text-gray-400 disabled:text-gray-200";

export const Pagination = ({
  className,
  totalPages,
  preventDefaultOnChange,
  onChange,
}: PaginationProps) => {
  const { currentPage, onPageChange: onPaginationContextPageChange } =
    usePaginationContext();

  const onPageChange = useCallback(
    (page: number) => {
      if (!preventDefaultOnChange) return onPaginationContextPageChange(page);

      return onChange(page, onPaginationContextPageChange);
    },
    [onPaginationContextPageChange, onChange, preventDefaultOnChange],
  );

  const pagination = usePagination({
    total: totalPages || 1,
    page: currentPage,
    onChange: onPageChange,
  });

  if (totalPages <= 1) {
    return null;
  }

  return (
    <nav
      className={clsx("flex flex-wrap justify-center sm:space-x-3", className)}
    >
      <div>
        <Button
          variant="text"
          className={arrowButtonClassName}
          disabled={pagination.active === 1}
          onClick={pagination.previous}
        >
          <ChevronLeftIcon className="h-6 w-6" />
        </Button>
      </div>

      {pagination.range.map((page, index) => (
        <div key={index}>
          {typeof page === "string" && (
            <Button
              disabled
              className="w-8 px-0 text-base disabled:border-transparent disabled:bg-transparent disabled:text-gray-700"
            >
              ...
            </Button>
          )}
          {typeof page === "number" && (
            <Button
              variant={page === pagination.active ? "primary" : "text"}
              className="w-8"
              onClick={() => {
                page !== pagination.active && pagination.setPage(page);
              }}
            >
              {page}
            </Button>
          )}
        </div>
      ))}

      <div>
        <Button
          variant="text"
          className={arrowButtonClassName}
          disabled={pagination.active === totalPages}
          onClick={pagination.next}
        >
          <ChevronRightIcon className="h-6 w-6" />
        </Button>
      </div>
    </nav>
  );
};
